import React, { useState, useEffect, useRef } from 'react';
import { createAndResizeCircImgDivs } from '../scripts/resizer';
import { scrollToHash } from '../scripts/scrollToHash.js';
import logo from "../assets/images/1_Intro/Logo_blue.svg"
import Header from '../components/Header.js';
import NavBar from '../components/NavBar.js';
import SectionContent from '../components/SectionContent';
import Banner from '../components/Banner.js';
import SurvivalSection from '../components/SurvivalSection.js';
import OurChoice from '../components/OurChoice.js';
import RightTrack from '../components/RightTrack.js';
import MeetMoment from '../components/MeetMoment.js';
import ScrollSection from '../components/ScrollSection.js';
import Footer from '../components/Footer.js';
import faceIcon from "../assets/images/1_Intro/intro_pivotal_face_icon.svg"
import ignoreIcon from "../assets/images/1_Intro/intro_pivotal_ignore_icon.svg"
import frequentIcon from "../assets/images/1_Intro/intro_pivotal_face1.jpg";
import reliableIcon from "../assets/images/1_Intro/intro_pivotal_face6.jpg";
import accessibleIcon from "../assets/images/1_Intro/intro_pivotal_face3.jpg";
import climateIcon from "../assets/images/1_Intro/intro_pivotal_face4.jpg";
import arrowIcon from "../assets/images/icon-arrow-blue.png";

function Home ( {handleOpenModal, handleCloseModal} ) {

    const [hasHdrModals, setHdrModals] = React.useState(false);
    // Since this is the homepage, we want to add has-hdr-modals to the body class list by default
    React.useEffect(() => {
        const body = document.body;
        const bodyClasses = body.classList;
        if (hasHdrModals) {
            bodyClasses.add('has-hdr-modals');
            setHdrModals(true);
        } 
        createAndResizeCircImgDivs();
    }, [hasHdrModals]);

    const [activeSlide, setActiveSlide] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);
    const slideInterval = useRef(null);
    const slides = [
        {
            imgSrc: frequentIcon,
            text: "More frequent. We will deliver more frequent subway, bus, and commuter rail service."
        },
        {
            imgSrc: reliableIcon,
            text: "More reliable. Customer journey time and on-time performance will exceed 90%."
        },
        {
            imgSrc: accessibleIcon,
            text: "More accessible. 95% of all commuter rail and subway stations will become accessible by 2045 and 2055, respectively."
        },
        {
            imgSrc: climateIcon,
            text: "More climate-ready. Greenhouse gas emissions will be reduced by 85% and vulnerable infrastructure will be protected against extreme weather."
        }
    ];
        

    useEffect(() => {
        if (isPlaying) {
            slideInterval.current = setInterval(() => {
                setActiveSlide((prevIndex) => (prevIndex + 1) % slides.length);
            }, 7000);
        }
        return () => clearInterval(slideInterval.current);
    }, [isPlaying, slides.length]);

    useEffect(() => {
        scrollToHash();
    }, []); 

    const handleDotClick = (index) => {
        setActiveSlide(index);
        setIsPlaying(false);
        clearInterval(slideInterval.current);
    };

    const handlePrevNextClick = (direction) => {
        setActiveSlide((prevIndex) => {
            const newIndex = prevIndex + direction;
            if (newIndex < 0) return slides.length - 1;
            if (newIndex >= slides.length) return 0;
            return newIndex;
        });
        setIsPlaying(false);
        clearInterval(slideInterval.current);
    };

    const handlePausePlayClick = () => {
        if (isPlaying) {
            clearInterval(slideInterval.current);
        } else {
            slideInterval.current = setInterval(() => {
                setActiveSlide((prevIndex) => (prevIndex + 1) % slides.length);
            }, 7000);
        }
        setIsPlaying(!isPlaying);
    };

    useEffect(() => {
        document.querySelectorAll('.rect-img').forEach((img) => {
            if (!img.nextElementSibling || !img.nextElementSibling.classList.contains('rect-img-div')) {
                const div = document.createElement('div');
                div.className = 'rect-img-div';
                div.style.backgroundImage = `url('${img.src}')`;
                img.after(div);
            }
        });
    }, [activeSlide]);

    return (
        <>
            <Header />
            <main id="content">
                <header>
                    <NavBar 
                    pageTitle={<p>20-Year Needs Assessment</p>} 
                    initialIsSubpage={false}
                    samePageLinks={[
                        ["#pivotal-moment", "A pivotal moment"], 
                        ["#our-choice", "Our choice"], 
                        ["/history", "Our history"], 
                        ["#our-vision", "Our goals"], 
                        ["#our-plan", "Our plan"]]} />
                </header>
                <div className="row" id="homepage-subheader">
                    <div className="col-md-12">
                        <h2>The future rides with us</h2> <br/>
                        <img src={logo} alt="" aria-hidden="true" />
                        <p>The 20-Year Needs Assessment is a broad, comprehensive blueprint that outlines the MTA region's transportation capital needs for the next generation. It provides an extensive, long-term view based upon rigorous data analysis across all the MTA agencies. It's also an opportunity to look beyond today's constraints to envision the possible future of the system if the right investments are made.</p>
                    </div>
                </div>
                <SectionContent
                    handleOpenModal={handleOpenModal}
                    handleCloseModal={handleCloseModal}
                />
                
                <div className="row teal-bg" id="facing-threats">
                    <Banner
                        id="facing-threats"
                        imgSrc={ignoreIcon}
                        text="If we ignore these threats ..."
                        backgroundColor="#EE352E"
                        textAlign="right"
                    />
                    <SurvivalSection />
                    <Banner
                        id="face-challenges"
                        imgSrc= {faceIcon}
                        text="If we face these challenges ..."
                        backgroundColor="#0039A6"
                        textAlign="left"
                    />        
                    <div className="col-md-12 future-vision">
                        <h4>In our vision of the future, we will bring riders back to transit and we will build for the future. We will accommodate our region's expected jobs and population growth of over 1 million in the next 20 years. In this vision, we will serve more than 8 million daily riders.</h4>
                        <h4>The MTA of the future will be ...</h4>
                        <div className="image-slideshow ss-blue" role="region" aria-label="Future Transit Benefits" aria-roledescription="carousel">
                            <button className="slideshow-trigger" aria-label={isPlaying ? "Stop automatic slide show" : "Start automatic slide show"} onClick={handlePausePlayClick}>
                                <i className={`fa ${isPlaying ? 'fa-pause' : 'fa-play'}`}></i>
                                <span>{isPlaying ? "Stop automatic slide show" : "Start automatic slide show"}</span>
                            </button>
                            <button className="slideshow-move prev" aria-label="Previous Slide" onClick={() => handlePrevNextClick(-1)}>
                                <img src={arrowIcon} alt="" aria-hidden="true" /><span>Previous Slide</span>
                            </button>
                            <button className="slideshow-move next" aria-label="Next Slide" onClick={() => handlePrevNextClick(1)}>
                                <img src={arrowIcon} alt="" aria-hidden="true" /><span>Next Slide</span>
                            </button>
                            <div className="image-slideshow-slides intro-only" aria-live="off">
                                {slides.map((slide, index) => (
                                    <div key={index} className={`slide ${index === activeSlide ? 'active fadeIn' : ''}`} aria-label={`${index + 1} of ${slides.length}`}>
                                        <img src={slide.imgSrc} className="rect-img" alt="" aria-hidden="true" />
                                        <p><strong>{slide.text.split('.')[0]}.</strong> {slide.text.split('.').slice(1).join('.')}</p>
                                    </div>
                                ))}
                            </div>
                            <ul>
                                {slides.map((_, index) => (
                                    <li key={index} className={index === activeSlide ? 'active' : ''}>
                                        <button onClick={() => handleDotClick(index)}>
                                            <span className="ada-element">See Slide {index + 1} of {slides.length}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
                <OurChoice />
                <RightTrack />
                <MeetMoment 
                    handleOpenModal={handleOpenModal}
                    handleCloseModal={handleCloseModal}/>
                <ScrollSection 
                    handleOpenModal={handleOpenModal}
                    handleCloseModal={handleCloseModal}
                    id="our-vision"
                    tabs={4}
                />
            </main>
            <Footer isHome={true} id="our-plan" class="homepage-footer" />
        </>
    );
};

export default Home;